import type { Restaurant, TimeSlot } from '../../core/model/restaurant';
import { addressSingleLine, hasRating, primaryPhoto } from '../../core/model/restaurant';
import { Formatters } from '../../core/util/formatters';
import { S } from '../../i18n/strings';
import { Photo } from './Photo';
import { StarIcon } from './icons';

/**
 * Tarjeta de restaurante.
 *
 * Cada bloque se compone **solo si hay dato**: sin calificación no se dibuja el bloque de
 * estrellas, sin foto se compone un marcador con texto en lugar de un hueco gris, y sin
 * distancia no aparece el chip. Nunca se pinta "N/A" ni un guion de relleno.
 */
export function RestaurantCard({
  restaurant,
  onOpen,
  onSelectSlot,
  showSlots = false,
}: {
  restaurant: Restaurant;
  onOpen: (id: string) => void;
  onSelectSlot?: (restaurant: Restaurant, slot: TimeSlot) => void;
  showSlots?: boolean;
}) {
  const photo = primaryPhoto(restaurant);
  const rating = Formatters.rating(restaurant.rating, restaurant.reviewCount);
  const price = Formatters.priceLevel(restaurant.priceLevel);
  const distance = Formatters.distance(restaurant.distanceMeters);
  const address = addressSingleLine(restaurant.address);

  return (
    <div className="restaurant-card">
      <button
        type="button"
        className="restaurant-card__media"
        onClick={() => onOpen(restaurant.id)}
        aria-label={restaurant.name}
        style={{ border: 0, padding: 0, cursor: 'pointer' }}
      >
        <Photo src={photo} alt={S.restaurantPhoto(restaurant.name)} />
        {restaurant.promotion && (
          <span className="chip chip--static chip--promo restaurant-card__promo">
            {restaurant.promotion.label}
          </span>
        )}
      </button>

      <div className="restaurant-card__body">
        <button
          type="button"
          onClick={() => onOpen(restaurant.id)}
          className="restaurant-card__name"
          style={{ border: 0, background: 'none', padding: 0, textAlign: 'left', cursor: 'pointer' }}
        >
          {restaurant.name}
        </button>

        <div className="meta-row">
          {/* El bloque completo desaparece si no hay reseñas que lo respalden. */}
          {hasRating(restaurant) && rating && (
            <span className="rating" aria-label={S.ratingLabel(rating)}>
              <StarIcon size={14} className="rating__star" />
              {rating}
              <span className="muted">({restaurant.reviewCount})</span>
            </span>
          )}
          {restaurant.cuisine && <MetaItem value={restaurant.cuisine} />}
          {price && <MetaItem value={price} />}
          {distance && <MetaItem value={S.restaurantDistance(distance)} />}
        </div>

        {address && <p className="muted">{address}</p>}

        {showSlots && (
          <div className="slot-row">
            {restaurant.previewSlots.length > 0 ? (
              restaurant.previewSlots.map((slot) => (
                <button
                  key={slot.token}
                  type="button"
                  className="slot"
                  disabled={!slot.available}
                  onClick={() => onSelectSlot?.(restaurant, slot)}
                >
                  {Formatters.time(slot.time)}
                </button>
              ))
            ) : (
              // "Sin mesas" es la respuesta a la pregunta del usuario, no un estado vacío que
              // se oculte: el restaurante sigue en la lista con su información.
              <span className="slot-empty">{S.availabilityNoneTitle}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MetaItem({ value }: { value: string }) {
  return (
    <>
      <span className="meta-row__dot" aria-hidden="true">
        ·
      </span>
      <span>{value}</span>
    </>
  );
}
